// Tax Rates — one row per parcel-year with the year's ACTUAL tax rate and where it came from (Philip 2026-10-27), for
// budget forecasting. Rates come only from bills we've worked through, or a rate typed from a notice:
//   Bill       the bill's own line rates, or its printed total rate
//   Effective  the bill prints no rate, so its ad valorem ÷ taxable (reports/taxRate effectiveBillRate)
//   Entered    no bill: the rate typed into the Tax Rate row (the card's Taxes-tab rate cell is the same rate)
// ⛔ ACTUAL RATES ONLY. No rate carried forward from another year and no estimate: a year with none reads blank. The
// other reports' EffectiveTaxRate column may carry one; this report never does.
//
// The rows are the shared parcel-year rows (../parcelYearRows — the same rate rule as every other report), the
// original notice's only: an added notice is not the year's bill.

import { fetchParcelYearScope, buildParcelYearRows } from '../parcelYearRows';
import { fetchByIds } from '../pagination';
import { rawValue } from '../format';

export const taxRates = {
  id: 'tax_rates',
  title: 'Tax Rates',
  description: 'Each parcel-year’s actual tax rate and its source (Bill, Effective or Entered), for budget forecasting.',

  filters: [
    'client',
    'years',
    { key: 'state', type: 'enum', label: 'State', from: 'state' },
    { key: 'rate_source', type: 'enum', label: 'Source', from: 'rate_source' },
    { key: 'property', type: 'enum', label: 'Property', from: 'property', picker: 'search', noun: 'properties' },
  ],

  columns: [
    { key: 'client', label: 'Client', type: 'text' },
    { key: 'property', label: 'Property', type: 'text' },
    { key: 'account_number', label: 'Parcel', type: 'text', locked: true },
    { key: 'state', label: 'State', type: 'text' },
    { key: 'tax_year', label: 'Year', type: 'year', locked: true },
    { key: 'billable_taxable', label: 'Taxable', type: 'value' },
    // A shared parcel's row is the client's share (parcelYearRows splitByProrata): its Taxable is that share, its rate the
    // parcel's. Off on screen, in the exports when the report has a pro-rata row.
    { key: 'pro_rata_pct', label: 'Pro-rata %', type: 'percent', optional: true, exportDefault: true, showWhenUsed: true },
    { key: 'tax_rate', label: 'Tax Rate', type: 'rate' },
    { key: 'rate_source', label: 'Source', type: 'text' },
  ],

  // The client names ride along: the Client column is on every row, one client or several.
  fetch: async (ctx) => {
    const [scope, clients] = await Promise.all([
      fetchParcelYearScope(ctx, 'tax rates'),
      fetchByIds('clients', 'id,name', 'id', ctx.clientIds || []),
    ]);
    return scope && { ...scope, clientNames: new Map(clients.map((c) => [c.id, c.name])) };
  },

  buildRows(raw, ctx) {
    return buildParcelYearRows(raw, ctx).filter((r) => !r.notice_no).map((r) => ({
      ...r,
      client: raw.clientNames.get(r.client_id) || r.client_id || '',
      // A plain number: the Source column says what kind of rate it is, so it carries no ‡ or † mark.
      tax_rate: rawValue(r.actual_rate),
    }));
  },

  emptyMessage: 'No parcel-years for this selection.',
};

export default taxRates;
